import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import clsx from 'clsx';
import { Button } from 'components/ui/Button';
import { BellIcon } from 'components/icons/customIcons';
import { ChatIcon } from 'components/icons/Icons';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationDto, EntityTypeEnum, ActivityFeedEntityLinkDto } from 'api/activity-feed';
import { ActivitySegments } from 'features/activity-feed/components/ActivitySegments';
import { ActivityContextChips } from 'features/activity-feed/components/ActivityContextChips';
import { useActivityRowUrl } from 'features/activity-feed/hooks/useActivityRowUrl';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import {
  parseMessage,
  getEntityUrl,
  getEntityLabel
} from 'features/activity-feed/utils/messageParser';
import {
  extractNameFromMessage,
  getActivityInitials,
  getInitials,
  formatRelativeTime,
  getAvatarColor
} from 'features/activity-feed/mocks/activityFeedMockData';

// ============================================
// ENTITY LINK COMPONENT
// ============================================

interface EntityLinkProps {
  entityType: EntityTypeEnum;
  entityId: string;
  displayText: string;
  riskId?: string;
  entities?: ActivityFeedEntityLinkDto[];
  onClick?: () => void;
}

const EntityLink: React.FC<EntityLinkProps> = ({
  entityType,
  entityId,
  displayText,
  riskId,
  entities,
  onClick
}) => {
  const navigate = useNavigate();
  const url = getEntityUrl(entityType, entityId, riskId, entities);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (url) {
      onClick?.();
      navigate(url);
    }
  };

  if (!url) {
    return <span className="text-gray-700">{displayText}</span>;
  }

  return (
    <button
      onClick={handleClick}
      className="text-primary hover:underline font-medium"
    >
      {displayText}
    </button>
  );
};

// ============================================
// NOTIFICATION ROW
// ============================================

interface NotificationRowProps {
  notification: NotificationDto;
  onMarkAsRead: (id: string) => void;
  onClose?: () => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  notification,
  onMarkAsRead,
  onClose
}) => {
  const navigate = useNavigate();

  // Avatar : initiales du souscripteur (context.clientName) quand on les a — cohérent
  // d'une ligne à l'autre. Repli : parsing heuristique du message.
  const initials = notification.context?.clientName
    ? getInitials(notification.context.clientName)
    : getActivityInitials(notification.message, notification.title);
  const avatarColor = getAvatarColor(initials);
  const relativeTime = formatRelativeTime(notification.createdAt);

  // Parse message to extract entity links
  const messageParts = useMemo(
    () => parseMessage(notification.message, notification.entities),
    [notification.message, notification.entities]
  );

  // Clic sur la ligne : demande de validation (valideur) / contractualisation du devis,
  // sinon dossier (via context) -> note -> fiche client, gaté par la matrice.
  const rowUrl = useActivityRowUrl(notification);

  // Même droit que les pastilles des listes : hors courtier, tout est présenté
  // comme lu (pas de style non-lu) et on ne marque jamais lu à sa place.
  const canNotifBadges = useRoleCan(Capability.ListNotificationBadges);
  const isUnread = canNotifBadges && !notification.isRead;

  const handleRowClick = () => {
    if (isUnread) {
      onMarkAsRead(notification.id);
    }
    if (rowUrl) {
      onClose?.();
      navigate(rowUrl);
    }
  };

  const handleEntityClick = () => {
    if (isUnread) {
      onMarkAsRead(notification.id);
    }
    onClose?.();
  };

  const renderMessage = () => {
    // Message riche (segments) : prioritaire — libellés humains, liens prêts.
    if (notification.segments && notification.segments.length > 0) {
      return (
        <ActivitySegments
          segments={notification.segments}
          riskId={notification.riskId ?? undefined}
          borrowerProjectId={notification.context?.borrowerProjectId ?? undefined}
        />
      );
    }

    const name = extractNameFromMessage(notification.message, notification.title);

    return messageParts.map((part, index) => {
      if (part.type === 'entity' && part.entityType && part.entityId) {
        const displayText = getEntityLabel(
          notification.entities,
          part.entityType,
          part.entityId
        );
        return (
          <EntityLink
            key={`${part.entityId}-${index}`}
            entityType={part.entityType}
            entityId={part.entityId}
            displayText={displayText}
            riskId={notification.riskId ?? undefined}
            entities={notification.entities}
            onClick={handleEntityClick}
          />
        );
      }

      // For text parts, check if it contains the name to make it bold
      if (part.type === 'text' && name && part.content.includes(name)) {
        const textParts = part.content.split(name);
        return (
          <React.Fragment key={index}>
            {textParts[0]}
            <span className="font-semibold">{name}</span>
            {textParts.slice(1).join(name)}
          </React.Fragment>
        );
      }

      return <span key={index}>{part.content}</span>;
    });
  };

  const hasNavigableEntity = Boolean(rowUrl);

  return (
    <div
      onClick={handleRowClick}
      className={clsx(
        'flex items-center gap-4 px-5 py-4 transition-colors duration-150',
        'hover:bg-gray-50',
        hasNavigableEntity && 'cursor-pointer',
        isUnread && 'bg-blue-50/40'
      )}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div
          className={clsx(
            'w-11 h-11 rounded-xl flex items-center justify-center',
            avatarColor
          )}
        >
          <span className="text-white font-semibold text-sm">{initials}</span>
        </div>
        {/* Unread indicator */}
        {isUnread && (
          <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-teal-400 border-2 border-white" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={clsx(
          'text-sm leading-snug',
          isUnread ? 'text-gray-700' : 'text-gray-600'
        )}>
          {renderMessage()}
        </p>
        {/* Ligne méta unique : chips de contexte (dédupliqués vs message) + date + courtier */}
        <ActivityContextChips
          context={notification.context}
          segments={notification.segments}
          onNavigate={handleEntityClick}
          after={
            <>
              {relativeTime}
              {/* Vue partagée : rattachement courtier (présent uniquement si envoyé) */}
              {notification.brokerName && <> · {notification.brokerName}</>}
            </>
          }
        />
      </div>
    </div>
  );
};

// ============================================
// SKELETON LOADER
// ============================================

const NotificationSkeleton: React.FC = () => (
  <div className="flex items-center gap-4 px-5 py-4 animate-pulse">
    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-200" />
    <div className="flex-1 min-w-0 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-1/4" />
    </div>
  </div>
);

// ============================================
// EMPTY STATE
// ============================================

const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <Bell className="w-8 h-8 text-gray-400" />
    </div>
    <p className="text-gray-600 font-medium">Aucune notification</p>
    <p className="text-gray-400 text-sm mt-1">Vous êtes à jour !</p>
  </div>
);

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<(() => void) | null>(null);

  const {
    notifications,
    unreadCount: rawUnreadCount,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    acknowledgeAll,
    refresh
  } = useNotifications({ pageSize: 10, autoLoad: false });

  // Même droit que les pastilles des listes : pas de compteur hors courtier.
  const canNotifBadges = useRoleCan(Capability.ListNotificationBadges);
  const unreadCount = canNotifBadges ? rawUnreadCount : 0;

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrollThreshold = 100;

    if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
      loadMore();
    }
  }, [isLoadingMore, hasMore, loadMore]);

  // Load notifications when dropdown opens
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Consultation pure (le POST ne marque plus rien) ; une fois les notifs
      // affichées avec succès, on marque tout lu (courtier uniquement — no-op
      // back sinon, mais inutile d'appeler) et le badge passe à 0.
      refresh().then((ok) => {
        if (ok && canNotifBadges) acknowledgeAll();
      });
    }
  }, [refresh, canNotifBadges, acknowledgeAll]);

  const handleClose = useCallback(() => {
    closeRef.current?.();
  }, []);

  return (
    <div className="flex gap-2">
      {/* Bouton Chat */}
      <Button color="slate" round>
        <ChatIcon />
      </Button>

      {/* Bouton Notifications avec Dropdown */}
      <Popover className="relative">
        {({ open, close }) => {
          // Store close function for use in child components
          closeRef.current = close;

          // Sync open state
          if (open !== isOpen) {
            handleOpenChange(open);
          }

          return (
            <>
              <PopoverButton as={React.Fragment}>
                <Button color="slate" round className="relative">
                  <BellIcon />
                  {/* Badge de notifications non lues */}
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-medium px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverButton>

              <PopoverPanel
                transition
                className={clsx(
                  'absolute right-0 z-50 mt-2 w-96 max-w-[calc(100vw-2rem)] origin-top-right',
                  'rounded-2xl bg-white shadow-xl ring-1 ring-black/5',
                  'transition duration-200 ease-out',
                  'data-[closed]:scale-95 data-[closed]:opacity-0'
                )}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                        {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <CheckCheck className="w-4 h-4" />
                      Tout marquer lu
                    </button>
                  )}
                </div>

                {/* Notifications List */}
                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className="max-h-[400px] overflow-y-auto"
                >
                  {isLoading ? (
                    // Loading skeletons
                    <div className="divide-y divide-gray-100">
                      {[...Array(4)].map((_, i) => (
                        <NotificationSkeleton key={i} />
                      ))}
                    </div>
                  ) : notifications.length === 0 ? (
                    // Empty state
                    <EmptyState />
                  ) : (
                    // Notifications list
                    <div className="divide-y divide-gray-100">
                      {notifications.map((notification) => (
                        <NotificationRow
                          key={notification.id}
                          notification={notification}
                          onMarkAsRead={markAsRead}
                          onClose={handleClose}
                        />
                      ))}

                      {/* Loading more indicator */}
                      {isLoadingMore && (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                        </div>
                      )}

                      {/* End of list */}
                      {!hasMore && notifications.length > 0 && (
                        <div className="py-4 text-center">
                          <span className="text-xs text-gray-400">
                            Fin des notifications
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </PopoverPanel>
            </>
          );
        }}
      </Popover>
    </div>
  );
};

export default NotificationDropdown;
